import axios from "axios";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Network-failure toast (frontend UX only). Deduped via a fixed sonner id + a short
// cooldown so a burst of failing requests shows one toast, never a loop. Does not touch
// auth refresh, status codes, retry behaviour, API contracts or permissions.
let lastNetworkToastAt = 0;
function notifyNetworkError() {
  const now = Date.now();
  if (now - lastNetworkToastAt < 4000) return;
  lastNetworkToastAt = now;
  toast.error("Network error", {
    id: "network-error",
    description: "Please check your connection and try again.",
  });
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const isAuthCall =
      original?.url?.includes("/auth/login") ||
      original?.url?.includes("/auth/register") ||
      original?.url?.includes("/auth/refresh");
    if (error.response?.status === 401 && !original?._retry && !isAuthCall) {
      original._retry = true;
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        return api(original);
      } catch {
        // refresh failed — session is gone
      }
    }
    // No HTTP response = network/offline/timeout (not a server status). Ignore cancels.
    if (!error.response && error.code !== "ERR_CANCELED") {
      notifyNetworkError();
    }
    return Promise.reject(error);
  },
);

export function formatApiError(error) {
  const detail = error?.response?.data?.detail;
  if (detail == null) return error?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
